import React, { useEffect, useState, useCallback } from "react";
import { adminClient } from "../../../lib/admin";
import { PackageSearch, Check, Ban, Shuffle, Users, Pause, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const badge = (v, map) => `text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${map[v] || "bg-black/10 text-black/60"}`;
const GENDER_STYLE = { men: "bg-blue-100 text-blue-700", women: "bg-pink-100 text-pink-700", unisex: "bg-black/10 text-black/60" };
const STATUS_STYLE = { in_stock: "bg-green-100 text-green-700", out_of_stock: "bg-red-100 text-red-700",
  valid: "bg-green-100 text-green-700", invalid: "bg-red-100 text-red-700",
  pass: "bg-green-100 text-green-700", review: "bg-amber-100 text-amber-700",
  clean: "bg-green-100 text-green-700", missing: "bg-red-100 text-red-700" };

export default function AdminNeedsReview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [collections, setCollections] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    adminClient().get("/admin/cj/needs-review")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error("Could not load review queue"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    adminClient().get("/cj/collections").then(({ data }) => setCollections((data.collections || []).map((c) => c.handle))).catch(() => {});
  }, [load]);

  const act = async (handle, action, target_collection) => {
    try {
      await adminClient().post(`/admin/cj/needs-review/${handle}/action`, { action, target_collection });
      toast.success(`${action} · ${handle.slice(0, 24)}…`);
      setItems((prev) => prev.filter((i) => i.handle !== handle));
      setSelected((prev) => { const n = new Set(prev); n.delete(handle); return n; });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const toggle = (h) => setSelected((prev) => { const n = new Set(prev); n.has(h) ? n.delete(h) : n.add(h); return n; });

  const bulkApprove = async () => {
    const chosen = items.filter((i) => selected.has(i.handle));
    const mapping = new Set(chosen.map((i) => i.suggested_collection));
    if (mapping.size !== 1) { toast.error("Bulk approve requires all selected to share one suggested collection"); return; }
    for (const i of chosen) await act(i.handle, i.suggested_collection === i.collection ? "approve" : "reassign", i.suggested_collection);
    toast.success(`Bulk approved ${chosen.length}`);
  };

  return (
    <div data-testid="admin-needs-review">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <PackageSearch className="w-5 h-5 text-taven-gold" />
          <h1 className="text-xl font-black font-display">Needs Review Queue</h1>
          <span className="text-xs text-muted-foreground">({items.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={bulkApprove} disabled={!selected.size} data-testid="review-bulk-approve"
            className="text-xs font-bold px-3 py-2 rounded-full bg-taven-black text-white disabled:opacity-40 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Bulk Approve ({selected.size})</button>
          <button onClick={load} data-testid="review-refresh" className="text-xs font-bold px-3 py-2 rounded-full border border-black/15 flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
        items.length === 0 ? <div className="border border-dashed border-black/15 py-16 text-center text-sm text-muted-foreground" data-testid="review-empty">No products awaiting review.</div> : (
        <div className="space-y-3">
          {items.map((i) => (
            <div key={i.handle} data-testid={`review-row-${i.handle}`} className="flex gap-4 border border-black/10 rounded-xl p-3 bg-white">
              <input type="checkbox" checked={selected.has(i.handle)} onChange={() => toggle(i.handle)} data-testid={`review-check-${i.handle}`} className="mt-1" />
              <img src={i.image} alt={i.title} className="w-20 h-24 object-cover rounded-lg bg-taven-gray shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold line-clamp-2">{i.title}</p>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  <span className={badge(i.gender, GENDER_STYLE)}>{i.gender}</span>
                  <span className={badge(i.inventory, STATUS_STYLE)}>{i.inventory}</span>
                  <span className={badge(i.pricing, STATUS_STYLE)}>{i.pricing}</span>
                  <span className={badge(i.compliance, STATUS_STYLE)}>compliance: {i.compliance}</span>
                  <span className={badge(i.image_quality, STATUS_STYLE)}>img: {i.image_quality}</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5">
                  <span>PID: {i.cj_pid}</span>
                  <span>SKU: {i.supplier_sku}</span>
                  <span>Type: {i.product_type || "—"}</span>
                  <span>Price: ${Number(i.retail_price || 0).toFixed(2)}</span>
                  <span>Current: <b>{i.collection}</b></span>
                  <span>Suggested: <b className="text-taven-gold">{i.suggested_collection}</b></span>
                  <span>Hold: {i.hold_reason}</span>
                  <span>Held: {(i.date_held || "").slice(0, 10)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2.5">
                  <button onClick={() => act(i.handle, i.suggested_collection === i.collection ? "approve" : "reassign", i.suggested_collection)}
                    data-testid={`review-approve-${i.handle}`} className="text-xs font-bold px-3 py-1.5 rounded-full bg-green-600 text-white flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Approve → {i.suggested_collection}</button>
                  <select data-testid={`review-reassign-${i.handle}`} defaultValue="" onChange={(e) => { if (e.target.value) act(i.handle, "reassign", e.target.value); }}
                    className="text-xs border border-black/15 rounded-full px-3 py-1.5 outline-none">
                    <option value="" disabled>Reassign to…</option>
                    {collections.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button onClick={() => act(i.handle, "unisex")} data-testid={`review-unisex-${i.handle}`} className="text-xs font-bold px-3 py-1.5 rounded-full border border-black/15 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Mark Unisex</button>
                  <button onClick={() => act(i.handle, "keep")} data-testid={`review-keep-${i.handle}`} className="text-xs font-bold px-3 py-1.5 rounded-full border border-black/15 flex items-center gap-1"><Pause className="w-3.5 h-3.5" /> Keep Held</button>
                  <button onClick={() => act(i.handle, "reject")} data-testid={`review-reject-${i.handle}`} className="text-xs font-bold px-3 py-1.5 rounded-full bg-red-600 text-white flex items-center gap-1"><Ban className="w-3.5 h-3.5" /> Reject</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
